import { type MutableRefObject } from 'react'
import { urlAPIv2 } from '../../urls'
import { getCommonHeaders } from '../../functions/shared/api/client'
import { endpoints } from '../../functions/shared/api/apiConfig'
import { type AppDispatch } from '../../store/store'

export type BatchMeta = {
    isLoading: boolean
    error: string | null
    loadedCount: number
    done: boolean
}

type BatchResult = { isSuccessful: boolean; success: boolean; error?: string }

type ActionCreator<T = any> = (payload: T) => any

type LoadEntityParams = {
    entityName: 'users' | 'units'
    endpoint: string
    pageSize: number
    runRef: MutableRefObject<number>
    abortRef: MutableRefObject<AbortController | null>
    seed: any[]
    metaRef: MutableRefObject<BatchMeta>
    dispatch: AppDispatch
    startActionCreator: () => any
    successActionCreator: ActionCreator<any[]>
    errorActionCreator: ActionCreator<{ message: string }>
    retryAttempts: number
    retryBaseDelayMs: number
    maxIterations: number
    stallLimit: number
}

const extractItems = (raw: any): any[] => {
    if (Array.isArray(raw)) return raw
    if (Array.isArray(raw?.data)) return raw.data
    if (Array.isArray(raw?.items)) return raw.items
    if (Array.isArray(raw?.results)) return raw.results
    if (Array.isArray(raw?.rows)) return raw.rows
    if (Array.isArray(raw?.response)) return raw.response
    if (Array.isArray(raw?.response?.data)) return raw.response.data
    if (Array.isArray(raw?.response?.items)) return raw.response.items
    if (Array.isArray(raw?.response?.results)) return raw.response.results
    if (Array.isArray(raw?.response?.rows)) return raw.response.rows
    if (Array.isArray(raw?.payload?.data)) return raw.payload.data
    return []
}

const toPositiveIntOrNull = (value: unknown): number | null => {
    const numeric = Number(value)
    if (!Number.isFinite(numeric) || numeric <= 0) return null
    return Math.floor(numeric)
}

const extractPageCount = (raw: any, pageSize: number): number | null => {
    const candidates = [
        raw?.meta?.pagination?.pageCount,
        raw?.response?.meta?.pagination?.pageCount,
        raw?.pagination?.pageCount,
        raw?.response?.pagination?.pageCount,
    ]

    for (const candidate of candidates) {
        const pageCount = toPositiveIntOrNull(candidate)
        if (pageCount != null) return pageCount
    }

    const totalCandidates = [
        raw?.meta?.pagination?.total,
        raw?.response?.meta?.pagination?.total,
        raw?.pagination?.total,
        raw?.response?.pagination?.total,
    ]

    for (const totalCandidate of totalCandidates) {
        const total = toPositiveIntOrNull(totalCandidate)
        if (total == null) continue
        return Math.max(1, Math.ceil(total / pageSize))
    }

    return null
}

const getEntityId = (item: any): number | null => {
    const id = Number(item?.id)
    return Number.isFinite(id) ? id : null
}

const sleep = async (ms: number): Promise<void> => {
    await new Promise((resolve) => setTimeout(resolve, ms))
}

const fetchPage = async (
    endpoint: string,
    page: number,
    pageSize: number,
    signal?: AbortSignal
): Promise<{ items: any[]; raw: any }> => {
    const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        withCount: 'true',
    })
    const response = await fetch(`${urlAPIv2}${endpoint}?${params.toString()}`, {
        method: 'GET',
        headers: getCommonHeaders(),
        signal,
    })
    const json = await response.json()
    if (!response.ok) {
        throw new Error(json?.error ?? `Failed to fetch page=${page}`)
    }
    return { items: extractItems(json), raw: json }
}

const fetchPageWithRetry = async (
    endpoint: string,
    page: number,
    pageSize: number,
    signal: AbortSignal,
    retryAttempts: number,
    retryBaseDelayMs: number
): Promise<{ items: any[]; raw: any }> => {
    let lastError: unknown = null
    for (let attempt = 1; attempt <= retryAttempts; attempt++) {
        if (signal.aborted) throw new DOMException('Aborted', 'AbortError')
        try {
            return await fetchPage(endpoint, page, pageSize, signal)
        } catch (error: unknown) {
            lastError = error
            if (signal.aborted) throw error
            if (attempt === retryAttempts) break
            const delay = retryBaseDelayMs * (2 ** (attempt - 1))
            await sleep(delay)
        }
    }
    throw lastError instanceof Error ? lastError : new Error('Page request failed')
}

const buildSeededCollection = (seed: any[]): { merged: any[]; seen: Set<number> } => {
    const seen = new Set<number>()
    const merged: any[] = []
    for (const item of seed) {
        const id = getEntityId(item)
        if (id == null) continue
        if (seen.has(id)) continue
        seen.add(id)
        merged.push(item)
    }
    return { merged, seen }
}

const loadEntityInBatches = async (params: LoadEntityParams): Promise<BatchResult> => {
    const {
        entityName,
        endpoint,
        pageSize,
        runRef,
        abortRef,
        seed,
        metaRef,
        dispatch,
        startActionCreator,
        successActionCreator,
        errorActionCreator,
        retryAttempts,
        retryBaseDelayMs,
        maxIterations,
        stallLimit,
    } = params

    const runId = runRef.current + 1
    runRef.current = runId
    const previousAbortController = abortRef.current
 
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    dispatch(startActionCreator())
    metaRef.current = { isLoading: true, error: null, loadedCount: 0, done: false }

    const seeded = buildSeededCollection(seed)
    let mergedItems = seeded.merged
    const seenItems = seeded.seen

    try {
        const firstPage = 1
        const first = await fetchPageWithRetry(endpoint, firstPage, pageSize, controller.signal, retryAttempts, retryBaseDelayMs)
        const firstBatch = first.items
        let knownPageCount = extractPageCount(first.raw, pageSize)

        for (const item of firstBatch) {
            const id = getEntityId(item)
            if (id == null) continue
            if (seenItems.has(id)) continue
            seenItems.add(id)
            mergedItems.push(item)
        }

        dispatch(successActionCreator(mergedItems))
        metaRef.current.loadedCount = mergedItems.length

        if (firstBatch.length > 0) {
            void (async () => {
                let page = firstPage + 1
                let iteration = 0
                let stalledBatches = 0
                let emptyBatches = 0
                const emptyBatchesToStop = 2
                while (true) {
                    if (runRef.current !== runId || controller.signal.aborted) {
                        return
                    }
                    if (iteration >= maxIterations) {
                        console.log(`[${entityName}] stop: max iterations reached`, { iteration, page, pageSize })
                        return
                    }
                    iteration += 1

                    const next = await fetchPageWithRetry(endpoint, page, pageSize, controller.signal, retryAttempts, retryBaseDelayMs)
                    const nextBatch = next.items
                    const nextPageCount = extractPageCount(next.raw, pageSize)
                    if (nextPageCount != null) knownPageCount = nextPageCount
                    if (runRef.current !== runId || controller.signal.aborted) {
                        console.log(`[${entityName}] stop reason: aborted-or-newer-run-after-fetch`, { runId, activeRunId: runRef.current, aborted: controller.signal.aborted, page, iteration })
                        return
                    }
                    if (nextBatch.length === 0) {
                        emptyBatches += 1
                        if (emptyBatches >= emptyBatchesToStop) {
                            console.log(`[${entityName}] stop reason: empty-batches-limit`, { page, emptyBatches, emptyBatchesToStop })
                            return
                        }
                        page += 1
                        continue
                    } 
                    emptyBatches = 0

                    let addedNow = 0
                    let nextMergedItems = mergedItems
                    for (const item of nextBatch) {
                        const id = getEntityId(item)
                        if (id == null) continue
                        if (seenItems.has(id)) continue
                        seenItems.add(id)
                        if (nextMergedItems === mergedItems) {
                            nextMergedItems = [...mergedItems]
                        }
                        nextMergedItems.push(item)
                        addedNow += 1
                    }
                    if (nextMergedItems !== mergedItems) {
                        mergedItems = nextMergedItems
                    }

                    dispatch(successActionCreator(mergedItems))
                    metaRef.current.loadedCount = mergedItems.length

                    if (addedNow === 0) {
                        stalledBatches += 1
                        const likelyTailBatch = nextBatch.length < pageSize
                        if (likelyTailBatch && stalledBatches >= stallLimit) {
                            console.log(`[${entityName}] stop reason: stalled-batches`, { stalledBatches, stallLimit, page, count: nextBatch.length })
                            return
                        }
                    } else {
                        stalledBatches = 0
                    }

                    page += 1
                }
            })().catch((error: unknown) => {
                if ((error as any)?.name !== 'AbortError') {
                    console.log(`Background ${entityName} load failed:`, error)
                    metaRef.current.error = error instanceof Error ? error.message : `Background ${entityName} load failed`
                }
            }).finally(() => {
                if (runRef.current === runId && !controller.signal.aborted) {
                    metaRef.current.isLoading = false
                    metaRef.current.done = true
                }
            })
        } else {
            metaRef.current.isLoading = false
            metaRef.current.done = true
        }

        return { isSuccessful: true, success: true }
    } catch (error: unknown) {
        if ((error as any)?.name === 'AbortError') {
            return { isSuccessful: false, success: false, error: 'aborted' }
        }
        const message = error instanceof Error ? error.message : `Failed to fetch ${entityName}`
        dispatch(errorActionCreator({ message }))
        metaRef.current = { isLoading: false, error: message, loadedCount: metaRef.current.loadedCount, done: false }
        return { isSuccessful: false, success: false, error: message }
    }
}

export const loadUsersInBatchesHelper = async (
    params: Omit<LoadEntityParams, 'entityName' | 'endpoint'>
): Promise<BatchResult> =>
    await loadEntityInBatches({
        ...params,
        entityName: 'users',
        endpoint: endpoints.adminRequests.users,
    })

export const loadUnitsInBatchesHelper = async (
    params: Omit<LoadEntityParams, 'entityName' | 'endpoint'>
): Promise<BatchResult> =>
    await loadEntityInBatches({
        ...params,
        entityName: 'units',
        endpoint: endpoints.adminRequests.units,
    })
